import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Alert, CircularProgress, Stack, Switch, FormControlLabel,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  listProjectGitlabConnections, createProjectGitlabConnection,
  updateProjectGitlabConnection, deleteProjectGitlabConnection,
  testProjectGitlabConnection, triggerProjectGitlabPullAll,
  type GitlabConnection,
} from '../api/adminApi';
import { listRegistryTypes, publishRegistry } from '../api/metaRegistriesApi';

interface Props {
  projectId: string;
}

interface ConnectionForm {
  name: string;
  gitlab_url: string;
  service_token: string;
  gitlab_project_id: string;
  branch: string;
  enabled: boolean;
}

const GITLAB_CLOUD_URL = 'https://gitlab.com';

type UrlMode = 'cloud' | 'custom';

const detectMode = (url: string): UrlMode =>
  !url || url === GITLAB_CLOUD_URL ? 'cloud' : 'custom';

const emptyForm: ConnectionForm = {
  name: '', gitlab_url: GITLAB_CLOUD_URL, service_token: '', gitlab_project_id: '', branch: 'main', enabled: true,
};

const ProjectGitlabSection: React.FC<Props> = ({ projectId }) => {
  const [connections, setConnections] = useState<GitlabConnection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [publishingConnId, setPublishingConnId] = useState<string | null>(null);

  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<GitlabConnection | null>(null);
  const [form, setForm] = useState<ConnectionForm>(emptyForm);
  const [urlMode, setUrlMode] = useState<UrlMode>('cloud');
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setConnections(await listProjectGitlabConnections(projectId));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setUrlMode('cloud');
    setFormError(null);
    setDialog(true);
  };

  const openEdit = (conn: GitlabConnection) => {
    setEditing(conn);
    const mode = detectMode(conn.gitlab_url);
    setUrlMode(mode);
    setForm({
      name: conn.name,
      gitlab_url: conn.gitlab_url || GITLAB_CLOUD_URL,
      service_token: '',
      gitlab_project_id: String(conn.gitlab_project_id ?? ''),
      branch: conn.branch ?? 'main',
      enabled: conn.enabled,
    });
    setFormError(null);
    setDialog(true);
  };

  const save = async () => {
    setFormError(null);
    try {
      const payload: Partial<GitlabConnection> = {
        name: form.name,
        gitlab_url: form.gitlab_url,
        gitlab_project_id: form.gitlab_project_id ? Number(form.gitlab_project_id) : undefined,
        branch: form.branch || 'main',
        enabled: form.enabled,
      };
      if (form.service_token) payload.service_token = form.service_token;

      if (editing) {
        await updateProjectGitlabConnection(projectId, editing.id, payload);
      } else {
        await createProjectGitlabConnection(projectId, payload);
      }
      setDialog(false);
      await load();
    } catch (e: any) {
      setFormError(e.message);
    }
  };

  const deleteConn = async (conn: GitlabConnection) => {
    if (!window.confirm(`Удалить подключение "${conn.name}"?`)) return;
    try {
      await deleteProjectGitlabConnection(projectId, conn.id);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const testConn = async (conn: GitlabConnection) => {
    try {
      const result = await testProjectGitlabConnection(projectId, conn.id);
      setSuccessMsg(result.success ? `✓ ${conn.name}: соединение успешно` : `✗ ${conn.name}: ${result.message}`);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const triggerPull = async (conn: GitlabConnection) => {
    try {
      await triggerProjectGitlabPullAll(projectId, conn.id);
      setSuccessMsg(`Импорт из GitLab запущен для "${conn.name}"`);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const publishAll = async (conn: GitlabConnection) => {
    setPublishingConnId(conn.id);
    setError(null);
    try {
      const registries = await listRegistryTypes(projectId);
      let totalPublished = 0;
      const errors: string[] = [];
      for (const reg of registries) {
        try {
          const result = await publishRegistry(projectId, reg.slug);
          totalPublished += result?.published ?? 0;
          if (result?.errors?.length) {
            errors.push(...result.errors.map((e: string) => `${reg.name}: ${e}`));
          }
        } catch (e: any) {
          errors.push(`${reg.name}: ${e.message}`);
        }
      }
      if (errors.length > 0) {
        setError(`Ошибки при публикации: ${errors.join('; ')}`);
      } else {
        setSuccessMsg(`Опубликовано ${totalPublished} записей в "${conn.name}"`);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPublishingConnId(null);
    }
  };

  return (
    <section style={{ marginBottom: 32 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>GitLab синхронизация</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={openCreate}>
          Добавить
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

      {loading ? <CircularProgress size={24} /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>GitLab Project ID</TableCell>
              <TableCell>Ветка</TableCell>
              <TableCell>Активно</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {connections.map((conn) => (
              <TableRow key={conn.id} hover>
                <TableCell>{conn.name}</TableCell>
                <TableCell sx={{ fontSize: 12, opacity: 0.8 }}>{conn.gitlab_url}</TableCell>
                <TableCell>{conn.gitlab_project_id ?? '—'}</TableCell>
                <TableCell>{conn.branch ?? 'main'}</TableCell>
                <TableCell>
                  <Chip label={conn.enabled ? 'Да' : 'Нет'} size="small" color={conn.enabled ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={() => testConn(conn)}>Тест</Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={publishingConnId === conn.id ? <CircularProgress size={12} /> : <CloudUploadOutlinedIcon fontSize="small" />}
                      disabled={publishingConnId === conn.id}
                      title="Опубликовать все реестры в GitLab"
                      onClick={() => publishAll(conn)}
                    >
                      В GitLab
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<CloudDownloadOutlinedIcon fontSize="small" />}
                      title="Импортировать изменения из GitLab"
                      onClick={() => triggerPull(conn)}
                    >
                      Из GitLab
                    </Button>
                    <IconButton size="small" onClick={() => openEdit(conn)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => deleteConn(conn)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {connections.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" variant="body2">Нет подключений</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редактировать подключение' : 'Добавить подключение'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <TextField
            label="Название" fullWidth margin="normal" required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.7 }}>
              Тип GitLab инстанса
            </Typography>
            <ToggleButtonGroup
              value={urlMode}
              exclusive
              onChange={(_, val) => {
                if (!val) return;
                setUrlMode(val);
                if (val === 'cloud') setForm((f) => ({ ...f, gitlab_url: GITLAB_CLOUD_URL }));
                else setForm((f) => ({ ...f, gitlab_url: '' }));
              }}
              size="small"
            >
              <ToggleButton value="cloud" sx={{ gap: 0.75, px: 2, textTransform: 'none' }}>
                <CloudOutlinedIcon fontSize="small" />
                Cloud (gitlab.com)
              </ToggleButton>
              <ToggleButton value="custom" sx={{ gap: 0.75, px: 2, textTransform: 'none' }}>
                <DnsOutlinedIcon fontSize="small" />
                Custom
              </ToggleButton>
            </ToggleButtonGroup>
            {urlMode === 'custom' && (
              <TextField
                label="Base URL инстанса GitLab" fullWidth margin="normal" required
                placeholder="https://gitlab.mycompany.com"
                value={form.gitlab_url}
                onChange={(e) => setForm((f) => ({ ...f, gitlab_url: e.target.value }))}
                helperText="Корневой URL без пути, например https://gitlab.mycompany.com"
              />
            )}
          </Box>
          <TextField
            label={editing ? 'Access Token (оставьте пустым, чтобы не менять)' : 'Access Token'}
            fullWidth margin="normal" type="password"
            value={form.service_token}
            onChange={(e) => setForm((f) => ({ ...f, service_token: e.target.value }))}
            helperText="Необходимые права: read_repository + write_repository (или api). Settings → Access Tokens → Add new token."
          />
          <TextField
            label="GitLab Project ID (числовой)" fullWidth margin="normal"
            value={form.gitlab_project_id}
            onChange={(e) => setForm((f) => ({ ...f, gitlab_project_id: e.target.value }))}
          />
          <TextField
            label="Ветка" fullWidth margin="normal"
            value={form.branch}
            onChange={(e) => setForm((f) => ({ ...f, branch: e.target.value }))}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            }
            label="Активно"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Отмена</Button>
          <Button variant="contained" onClick={save}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default ProjectGitlabSection;
